// The schedule errors both clients share live in the shared package (one class
// identity, so `instanceof` checks match across app and shared code); the
// save/import/push errors below serve scheduler-only operations and stay here.
export {
  ScheduleNotImportedError,
  ScheduleSiteUnavailableError,
  scheduleRetry,
  throwForError,
  throwForNotImported,
} from '@d-orca/shared/client/apis';

export type ScheduleSaveErrorCode =
  | 'SCHEDULE_CONFLICT' // schedule changed since load (D-ORCA version) — reload & redo
  | 'COMMITTED_TASK_HAS_WORK' // save deletes a committed task with recorded work — restore it
  | 'SCHEDULE_LOCKED' // push-to-Genba: open in the Genba web editor — retry later
  | 'SCHEDULE_SAVE_REJECTED' // push-to-Genba: upstream import rejected the file
  | 'UNKNOWN';

/**
 * A failed save (PUT to D-ORCA) or a failed push-to-Genba. `messages` carries
 * upstream error details / violations (may be empty). The Genba-only codes
 * (LOCKED / SAVE_REJECTED) can now only arise on the push path — the D-ORCA save
 * (PUT) accepts every edit and never returns them.
 */
export class ScheduleSaveError extends Error {
  constructor(
    public readonly code: ScheduleSaveErrorCode,
    message: string,
    public readonly messages: string[] = [],
  ) {
    super(message);
    this.name = 'ScheduleSaveError';
  }
}

/**
 * An import was requested without `overwrite` on a schedule that is already
 * seeded. The picker normally avoids this by checking the `imported` flag; if it
 * still happens, the page can offer an overwrite (re-import).
 */
export class ScheduleAlreadyImportedError extends Error {
  constructor(public readonly scheduleNumber: string, message: string) {
    super(message);
    this.name = 'ScheduleAlreadyImportedError';
  }
}
